import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import PCMBadge from "../../components/PCMBadge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  FileText,
  Lightbulb,
  Target,
  Anchor,
  Video,
} from "lucide-react";

import { API } from "../../utils/api";

const PCM_TYPES = ["Analyseur", "Empathique", "Persévérant", "Énergiseur", "Promoteur", "Imagineur"];
const BLOCK_TYPES = [
  { value: "text", label: "Texte", icon: FileText },
  { value: "question", label: "Question", icon: Lightbulb },
  { value: "anchor", label: "Ancrage", icon: Anchor },
];
const TARGET_TYPES = [
  { value: "common", label: "Commun (tous)" },
  { value: "base", label: "Par Base" },
  { value: "phase", label: "Par Phase" },
];

const AdminContent = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [isExerciseDialogOpen, setIsExerciseDialogOpen] = useState(false);
  const [blockForm, setBlockForm] = useState({
    type: "text",
    title: "",
    content: "",
    target_type: "common",
    target_value: "",
  });
  const [exerciseForm, setExerciseForm] = useState({
    title: "",
    description: "",
    instructions: "",
    target_phase: "",
    is_optional: false,
  });

  useEffect(() => {
    fetchModule();
  }, [moduleId, token]);

  const fetchModule = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const modules = await response.json();
        const foundModule = modules.find((m) => m.module_id === moduleId);
        if (foundModule) {
          setModule(foundModule);
        } else {
          toast.error("Module non trouvé");
          navigate("/admin/modules");
        }
      }
    } catch (error) {
      console.error("Error fetching module:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/modules/${moduleId}/blocks`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...blockForm,
          target_value: blockForm.target_type === "common" ? null : blockForm.target_value,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Bloc ajouté");
        setIsBlockDialogOpen(false);
        setBlockForm({ type: "text", title: "", content: "", target_type: "common", target_value: "" });
        fetchModule();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'ajout");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!confirm("Supprimer ce bloc ?")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules/${moduleId}/blocks/${blockId}`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Bloc supprimé");
        fetchModule();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleAddExercise = async (e) => {
    e.preventDefault();
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/modules/${moduleId}/exercises`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...exerciseForm,
          target_phase: exerciseForm.target_phase || null,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Exercice ajouté");
        setIsExerciseDialogOpen(false);
        setExerciseForm({ title: "", description: "", instructions: "", target_phase: "", is_optional: false });
        fetchModule();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'ajout");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    if (!confirm("Supprimer cet exercice ?")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules/${moduleId}/exercises/${exerciseId}`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Exercice supprimé");
        fetchModule();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getBlocksByType = (type) => {
    return module?.content_blocks?.filter((b) => b.type === type) || [];
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  if (!module) return null;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 p-6 lg:p-8 lg:ml-64" data-testid="admin-content">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin/modules")}
            className="flex items-center text-slate-600 hover:text-[#101E4D] mb-4"
            data-testid="back-to-modules"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour aux modules
          </button>
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-[#101E4D] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-2xl">{module.number}</span>
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D]">{module.title}</h1>
              <p className="text-slate-600">Gestion du contenu du module</p>
            </div>
          </div>
        </div>

        {/* Module Info */}
        <Card className="mb-6 bg-white">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3">
                <Video className="w-5 h-5 text-[#FF6B35]" />
                <div>
                  <p className="text-sm text-slate-500">Vidéo</p>
                  <p className="font-medium text-[#101E4D]">
                    {module.video_url ? "Configurée" : "Non configurée"}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-[#FF6B35]" />
                <div>
                  <p className="text-sm text-slate-500">Blocs de contenu</p>
                  <p className="font-medium text-[#101E4D]">{module.content_blocks?.length || 0}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Target className="w-5 h-5 text-[#FF6B35]" />
                <div>
                  <p className="text-sm text-slate-500">Exercices</p>
                  <p className="font-medium text-[#101E4D]">{module.exercises?.length || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="blocks" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger
              value="blocks"
              className="data-[state=active]:bg-[#101E4D] data-[state=active]:text-white"
              data-testid="tab-blocks"
            >
              Blocs de contenu
            </TabsTrigger>
            <TabsTrigger
              value="exercises"
              className="data-[state=active]:bg-[#101E4D] data-[state=active]:text-white"
              data-testid="tab-exercises"
            >
              Exercices
            </TabsTrigger>
          </TabsList>

          {/* Content Blocks Tab */}
          <TabsContent value="blocks">
            <div className="flex justify-end mb-4">
              <Dialog open={isBlockDialogOpen} onOpenChange={setIsBlockDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                    data-testid="add-block-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un bloc
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Ajouter un bloc de contenu</DialogTitle>
                    <DialogDescription>
                      Créez un bloc personnalisé selon la Base ou Phase PCM
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddBlock} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Type de bloc</Label>
                        <Select
                          value={blockForm.type}
                          onValueChange={(v) => setBlockForm({ ...blockForm, type: v })}
                        >
                          <SelectTrigger data-testid="block-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {BLOCK_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Ciblage</Label>
                        <Select
                          value={blockForm.target_type}
                          onValueChange={(v) => setBlockForm({ ...blockForm, target_type: v, target_value: "" })}
                        >
                          <SelectTrigger data-testid="block-target-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TARGET_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {blockForm.target_type !== "common" && (
                      <div className="space-y-2">
                        <Label>
                          {blockForm.target_type === "base" ? "Base PCM" : "Phase PCM"}
                        </Label>
                        <Select
                          value={blockForm.target_value}
                          onValueChange={(v) => setBlockForm({ ...blockForm, target_value: v })}
                        >
                          <SelectTrigger data-testid="block-target-value">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {PCM_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input
                        value={blockForm.title}
                        onChange={(e) => setBlockForm({ ...blockForm, title: e.target.value })}
                        required
                        data-testid="block-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contenu</Label>
                      <Textarea
                        value={blockForm.content}
                        onChange={(e) => setBlockForm({ ...blockForm, content: e.target.value })}
                        rows={5}
                        required
                        data-testid="block-content"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#101E4D] hover:bg-[#0A1330]"
                      data-testid="submit-block"
                    >
                      Ajouter le bloc
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Blocks List by Type */}
            <div className="space-y-6">
              {BLOCK_TYPES.map(({ value, label, icon: Icon }) => (
                <Card key={value} className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg text-[#101E4D]">
                      <Icon className="w-5 h-5 mr-2 text-[#FF6B35]" />
                      {label}s ({getBlocksByType(value).length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {getBlocksByType(value).length > 0 ? (
                      <div className="space-y-3">
                        {getBlocksByType(value).map((block) => (
                          <div
                            key={block.block_id}
                            className="flex items-start justify-between p-4 bg-slate-50 rounded-lg"
                            data-testid={`block-${block.block_id}`}
                          >
                            <div className="flex-1 mr-4">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="font-medium text-[#101E4D]">{block.title}</h4>
                                {block.target_type !== "common" && block.target_value && (
                                  <PCMBadge type={block.target_value} size="sm" />
                                )}
                                {block.target_type === "common" && (
                                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                    Commun
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 line-clamp-2">{block.content}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteBlock(block.block_id)}
                              data-testid={`delete-block-${block.block_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm py-4 text-center">
                        Aucun bloc de ce type
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Exercises Tab */}
          <TabsContent value="exercises">
            <div className="flex justify-end mb-4">
              <Dialog open={isExerciseDialogOpen} onOpenChange={setIsExerciseDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                    data-testid="add-exercise-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un exercice
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Ajouter un exercice</DialogTitle>
                    <DialogDescription>
                      Créez un exercice ciblé par Phase PCM
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddExercise} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input
                        value={exerciseForm.title}
                        onChange={(e) => setExerciseForm({ ...exerciseForm, title: e.target.value })}
                        required
                        data-testid="exercise-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={exerciseForm.description}
                        onChange={(e) => setExerciseForm({ ...exerciseForm, description: e.target.value })}
                        rows={2}
                        required
                        data-testid="exercise-description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instructions</Label>
                      <Textarea
                        value={exerciseForm.instructions}
                        onChange={(e) => setExerciseForm({ ...exerciseForm, instructions: e.target.value })}
                        rows={4}
                        required
                        data-testid="exercise-instructions"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phase PCM ciblée (optionnel)</Label>
                      <Select
                        value={exerciseForm.target_phase}
                        onValueChange={(v) => setExerciseForm({ ...exerciseForm, target_phase: v })}
                      >
                        <SelectTrigger data-testid="exercise-phase">
                          <SelectValue placeholder="Tous les profils" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Tous les profils</SelectItem>
                          {PCM_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="is_optional"
                        checked={exerciseForm.is_optional}
                        onCheckedChange={(checked) =>
                          setExerciseForm({ ...exerciseForm, is_optional: checked })
                        }
                        data-testid="exercise-optional"
                      />
                      <Label htmlFor="is_optional" className="text-sm">
                        Exercice optionnel
                      </Label>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#101E4D] hover:bg-[#0A1330]"
                      data-testid="submit-exercise"
                    >
                      Ajouter l'exercice
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center text-lg text-[#101E4D]">
                  <Target className="w-5 h-5 mr-2 text-[#FF6B35]" />
                  Exercices ({module.exercises?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {module.exercises?.length > 0 ? (
                  <div className="space-y-4">
                    {module.exercises.map((exercise) => (
                      <div
                        key={exercise.exercise_id}
                        className="p-5 bg-slate-50 rounded-xl"
                        data-testid={`exercise-${exercise.exercise_id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-[#101E4D]">{exercise.title}</h4>
                            {exercise.target_phase && (
                              <PCMBadge type={exercise.target_phase} size="sm" />
                            )}
                            {exercise.is_optional && (
                              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                Optionnel
                              </span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteExercise(exercise.exercise_id)}
                            data-testid={`delete-exercise-${exercise.exercise_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">{exercise.description}</p>
                        <div className="p-3 bg-white rounded-lg border border-slate-200">
                          <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide">
                            Instructions
                          </p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {exercise.instructions}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucun exercice configuré</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminContent;
